import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  model,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-input-mask',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiInputMask),
      multi: true,
    },
  ],
  template: `
    <input
      [id]="inputId()"
      type="text"
      [placeholder]="placeholder()"
      [disabled]="disabled()"
      [class]="inputClasses()"
      [value]="displayValue()"
      (input)="onInput($event)"
      (blur)="onTouched()"
      [attr.aria-label]="ariaLabel() || placeholder()"
      [attr.aria-disabled]="disabled()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiInputMask {
  readonly mask = input<string>('');
  readonly placeholder = input<string>('');
  readonly disabled = input<boolean>(false);
  readonly inputId = input<string>('input-' + Math.random().toString(36).substring(2, 9));
  readonly ariaLabel = input<string>('');
  readonly styleClass = input<string>('');

  readonly value = model<string>('');

  onTouched: () => void = () => {};
  private onChange: (value: string) => void = () => {};

  protected readonly displayValue = computed(() => {
    return this.applyMask(this.value());
  });

  protected readonly inputClasses = computed(() => {
    const base =
      'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 hover:border-slate-300 placeholder:text-slate-400';
    const disabled = this.disabled() ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-white';
    return `${base} ${disabled} ${this.styleClass()}`.trim();
  });

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value.replace(/[^0-9a-zA-Z]/g, '');
    this.value.set(raw);
    this.onChange(raw);
  }

  private applyMask(value: string): string {
    if (!value || !this.mask()) return value;
    const maskChars = this.mask().split('');
    let result = '';
    let valueIndex = 0;

    for (const maskChar of maskChars) {
      if (valueIndex >= value.length) break;
      if (maskChar === '9' || maskChar === 'a' || maskChar === '*') {
        result += value[valueIndex];
        valueIndex++;
      } else {
        result += maskChar;
      }
    }
    return result;
  }

  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // handled via input
  }
}
